// Hierarquia de categorias do ERP por id do pai (codCategoriaSup), em qualquer profundidade.
// O Conta Azul passou a aceitar 3 níveis (topo → intermediária → folha); estes helpers
// substituem os agrupamentos que assumiam "pai direto = raiz".

import { isRootParent, ZERO_GUID } from "./categoryTree";

export { isRootParent, ZERO_GUID };

// ERPs cujo plano de contas pode ter mais de 2 níveis por id de pai (Conta Azul V1/V2 e Vehro,
// que usa o mesmo gold do CA V2). Nos demais o agrupamento continua pelo pai direto.
export const ERPS_COM_CATEGORIA_MULTINIVEL = [1, 5, 7];
const ERPS_COM_CATEGORIA_MULTINIVEL_NOMES = ["contaazul", "contaazulv2", "vehro"];

/** Aceita o código numérico (sync.erp) ou o nome do enum (ex.: "ContaAzulV2" no Planejamento). */
export const supportsMultiLevelCategories = (erp) => {
  if (erp === null || erp === undefined || erp === "") return false;
  if (ERPS_COM_CATEGORIA_MULTINIVEL.includes(Number(erp))) return true;
  return ERPS_COM_CATEGORIA_MULTINIVEL_NOMES.includes(String(erp).trim().toLowerCase());
};

const normalize = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

/**
 * Ids dos ancestrais de `code`, do topo até o pai direto (sem incluir o próprio code).
 * `parentByCode` deve conter todas as categorias conhecidas: um pai fora dele (órfão) encerra a
 * cadeia, e o último nível conhecido vira o topo. Protege contra ciclo.
 */
export const ancestorCodesOf = (code, parentByCode, maxDepth = 10) => {
  const chain = [];
  const seen = new Set([normalize(code)]);
  let current = normalize(parentByCode.get(normalize(code)));
  while (
    !isRootParent(current) &&
    parentByCode.has(current) &&
    !seen.has(current) &&
    chain.length < maxDepth
  ) {
    chain.unshift(current);
    seen.add(current);
    current = normalize(parentByCode.get(current));
  }
  return chain;
};

const compareLabel = (a, b) =>
  String(a || "").localeCompare(String(b || ""), "pt-BR", { sensitivity: "base" });

/**
 * Agrupa categorias (folhas) numa árvore de grupos pelos seus ancestrais.
 *
 * - allCategories: lista completa [{ codCategoria, codCategoriaSup, descricao }] usada para
 *   resolver pais/rótulos (pode conter categorias que não estão em `leaves`).
 * - multiLevel=false mantém o comportamento antigo: um único grupo pelo pai direto.
 *
 * Retorna grupos { key, label, depth, categories (folhas diretas), subgroups,
 * allCategories (todas as folhas descendentes) }, ordenados por rótulo.
 */
export function groupLeavesByAncestors(leaves, allCategories, { multiLevel = false } = {}) {
  const byCode = new Map();
  const parentByCode = new Map();
  (allCategories || []).forEach((c) => {
    const code = normalize(c.codCategoria);
    if (!code) return;
    byCode.set(code, c);
    parentByCode.set(code, normalize(c.codCategoriaSup));
  });

  const roots = new Map();
  const groupsByKey = new Map();

  const ensureGroup = (key, label, parentGroup) => {
    if (groupsByKey.has(key)) return groupsByKey.get(key);
    const group = {
      key,
      label,
      depth: parentGroup ? parentGroup.depth + 1 : 0,
      categories: [],
      subgroups: [],
      allCategories: [],
    };
    groupsByKey.set(key, group);
    if (parentGroup) parentGroup.subgroups.push(group);
    else roots.set(key, group);
    return group;
  };

  (leaves || []).forEach((cat) => {
    const code = normalize(cat.codCategoria);
    const parentCode = normalize(cat.codCategoriaSup);

    let path;
    // Mesmas chaves/rótulos do agrupamento antigo: só pai vazio é "Sem categoria superior";
    // Guid.Empty (não existe como categoria) cai em "Categoria superior não encontrada".
    if (!parentCode) {
      path = [{ key: "no-parent", label: "Sem categoria superior" }];
    } else if (!byCode.has(parentCode)) {
      path = [{ key: parentCode, label: "Categoria superior não encontrada" }];
    } else {
      const codes = multiLevel ? ancestorCodesOf(code, parentByCode) : [parentCode];
      path = (codes.length ? codes : [parentCode]).map((c) => ({
        key: c,
        label: byCode.get(c)?.descricao || "Categoria superior não encontrada",
      }));
    }

    let group = null;
    path.forEach((step) => {
      group = ensureGroup(step.key, step.label, group);
      group.allCategories.push(cat);
    });
    group.categories.push(cat);
  });

  const sortGroup = (group) => {
    group.categories.sort((a, b) => compareLabel(a.descricao, b.descricao));
    group.allCategories.sort((a, b) => compareLabel(a.descricao, b.descricao));
    group.subgroups.sort((a, b) => compareLabel(a.label, b.label));
    group.subgroups.forEach(sortGroup);
    return group;
  };

  return Array.from(roots.values())
    .map(sortGroup)
    .sort((a, b) => compareLabel(a.label, b.label));
}

/** Achata a árvore de grupos (pré-ordem), útil para lookup por key. */
export function flattenGroups(groups) {
  const out = [];
  const walk = (list) =>
    (list || []).forEach((g) => {
      out.push(g);
      walk(g.subgroups);
    });
  walk(groups);
  return out;
}
